import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import GenericFormPopup from "../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import {
  driverIssueFieldConfiguration,
  driverIssueInitialValues,
} from "../../../formConfigs/Recruiting/driverIssueFieldConfig";
import { DriverIssueDto } from "../../../interfaces/Recruiting/driverIssue";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const fetchDrivers = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data;
};

const fetchTrucks = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrucks`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data;
};

const getUnitNumber = (fieldValue: any) => {
  if (
    fieldValue &&
    typeof fieldValue === "object" &&
    "unitNumber" in fieldValue
  ) {
    return fieldValue.unitNumber;
  }
  return undefined;
};

const getDriverName = (fieldValue: any) => {
  if (fieldValue && typeof fieldValue === "object" && "label" in fieldValue) {
    return fieldValue.label;
  }
  if (typeof fieldValue === "string") {
    return fieldValue;
  }
  return "";
};

const formatDateValue = (value?: string | null) => {
  if (!value) return "";
  return value.split("T")[0];
};

interface UpdateDriverIssuePopupProps {
  issue: DriverIssueDto;
}

const UpdateDriverIssuePopup = ({ issue }: UpdateDriverIssuePopupProps) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<any>(
    driverIssueInitialValues
  );
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: drivers = [] } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchDrivers(token),
    enabled: open,
  });

  const { data: trucks = [] } = useQuery({
    queryKey: ["trucks"],
    queryFn: () => fetchTrucks(token),
    enabled: open,
  });

  const autocompleteData = useMemo(
    () => ({
      drivers: (drivers as any[]).map((driver) => ({
        id: driver.id?.toString?.() ?? driver.id,
        label: `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim(),
      })),
      trucks: (trucks as any[]).map((truck) => ({
        id: truck.id?.toString?.() ?? truck.id,
        label: `${truck.unitNumber ?? ""} | ${truck.make ?? ""} ${
          truck.model ?? ""
        } ${truck.year ?? ""}`.trim(),
        unitNumber: truck.unitNumber ?? "",
      })),
    }),
    [drivers, trucks]
  );

  useEffect(() => {
    if (!issue) return;

    const selectedDriver =
      autocompleteData.drivers.find((driver) => driver.id === issue.driverId) ||
      (issue.driverId || issue.driverName
        ? {
            id: issue.driverId ?? issue.driverName,
            label: issue.driverName ?? "Assigned Driver",
          }
        : null);

    const selectedTruck =
      autocompleteData.trucks.find((truck) => truck.id === issue.truckId) ||
      (issue.truckId
        ? {
            id: issue.truckId,
            label: issue.truckUnitNumber ?? "Assigned Truck",
            unitNumber: issue.truckUnitNumber ?? "",
          }
        : null);

    setInitialValues({
      ...driverIssueInitialValues,
      ...issue,
      driverId: selectedDriver,
      truckId: selectedTruck,
      dateOfIssueNotification: formatDateValue(issue.dateOfIssueNotification),
      resolutionDate: formatDateValue(issue.resolutionDate ?? undefined),
    });
  }, [issue, autocompleteData, open]);

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/recruiting/driver-issue`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["driverIssues"] });
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update driver issue");
    },
  });

  const submitHandler = (data: any) => {
    updateMutation.mutate({
      ...data,
      id: issue.id,
      driverId: getId(data.driverId),
      truckId: getId(data.truckId),
      driverName: getDriverName(data.driverId),
      truckUnitNumber: getUnitNumber(data.truckId),
      resolutionDate: data.resolutionDate || null,
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Update
      </Button>
      <GenericFormPopup
        formFields={driverIssueFieldConfiguration}
        initialValues={initialValues}
        title="Update Driver Issue"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        autocompleteData={autocompleteData}
        loading={updateMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Driver issue updated successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default UpdateDriverIssuePopup;
